import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogContent, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogSection } from 'src/basic-components/dialog-section';
import { FormikTextView } from 'src/basic-components/formik-inputs';
import { FormikDateView } from 'src/basic-components/formik-inputs/formik-date-view';
import { FormikSelectView } from 'src/basic-components/formik-inputs/formik-select-view';
import { FormikTextAreaView } from 'src/basic-components/formik-inputs/formik-text-area-view';
import T from 'src/basic-components/text';
import Iconify from 'src/components/Iconify';
import { useApi } from 'src/contexts/api-provider';
import { useNotify } from 'src/hooks/use-notify';
import useResponsive from 'src/hooks/useResponsive';
import { CombinedMonths, Years } from 'src/utils/enums';
import { persianMonths } from 'src/utils/persian-calendar';
import * as Yup from 'yup';

export const ManageTodoModal = ({ data, open, onSave, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { CoreApi } = useApi();
  const { t } = useTranslation();
  const notify = useNotify();
  const isDesktop = useResponsive('up', 'lg');
  const apiName = 'adminTodoApi';
  const api = {
    saveTodo: CoreApi(apiName, 'saveTodo'),
  };

  const validationSchema = Yup.object({
    text: Yup.string().required(t('required')),
    dueYear: Yup.number().notOneOf([0], ' ').required(t('required')),
    dueMonth: Yup.number().notOneOf([0], ' ').required(t('required')),
    //statusId: Yup.string().required(t('required')),
  });

  return (
    <Dialog open={open} onClose={onClose}>
      <Formik
        initialValues={{
          id: data?.id,
          text: data?.text || '',

          dueYear: data?.dueYear || 1404,
          dueMonth: data?.dueMonth || 1,
          statusId: data?.statusId || '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          setIsLoading(true);
          api
            .saveTodo(values)
            .then((model) => {
              onSave(model.todo);
              notify.success(t('changes-were-successfully-saved'));
            })
            .catch(notify.error)
            .finally(() => setIsLoading(false));
        }}
      >
        {(formik) => {
          const insertMode = !data?.id;

          return (
            <Form>
              <DialogSection title={!insertMode ? <T>edit</T> : <T>add</T>}>
                <DialogContent sx={{ m: 0, p: 1 }}>
                  <Stack flex flexDirection="column" gap={2} sx={{ flexFlow: 'wrap' }}>
                    <FormikTextView name="text" label="todo-text" formik={formik} focused={true} />

                    <Stack
                      flex
                      flexDirection="row"
                      gap={1}
                      flexWrap={isDesktop ? 'nowrap' : 'wrap'}
                      justifyContent="center"
                      sx={{ width: '100%' }}
                    >
                      <FormikSelectView
                        name="dueYear"
                        label="due-year"
                        type="number"
                        formik={formik}
                        items={[{ id: 0, title: '' }, ...Years]}
                        style={{ flexGrow: 1, width: isDesktop ? '100%' : '11rem' }}
                      />

                      <FormikSelectView
                        items={[{ id: 0, title: '' }, ...persianMonths]}
                        name="dueMonth"
                        label="due-month"
                        type="number"
                        formik={formik}
                        style={{ flexGrow: 1, width: isDesktop ? '100%' : '11rem' }}
                      />
                    </Stack>
                    <Stack flex flexDirection="row" justifyContent="end" gap={1} sx={{ width: '100%' }}>
                      {!insertMode && (
                        <FormikSelectView
                          name="statusId"
                          label="status"
                          items={[
                            { id: 'open', title: t('open') },

                            {
                              id: 'pending',
                              title: t('pending'),
                            },
                            {
                              id: 'completed',
                              title: t('completed'),
                            },
                          ]}
                          formik={formik}
                        />
                      )}
                    </Stack>
                    <Stack flex flexDirection="row" justifyContent="end" gap={1} sx={{ width: '100%' }}>
                      <Button type="button" variant="outlined" onClick={onClose}>
                        <T>cancel</T>
                      </Button>
                      <LoadingButton loading={isLoading} type="submit" variant="contained">
                        <T>apply</T>
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </DialogContent>
              </DialogSection>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};
